import React, { useState, useContext, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FiSearch,
  FiPlus,
  FiRefreshCw,
  FiX,
  FiUser,
  FiStar,
  FiTrendingUp,
  FiZap,
  FiClock,
  FiUsers,
  FiGrid,
  FiChevronRight,
  FiLayers,
  FiChevronDown,
  FiFilter,
  FiTarget,
} from "react-icons/fi";
import { UserContext } from "../../context/UserContext";
import { useRoom } from "../../context/RoomContext";
import { useDiscovery, useDiscoverySearch } from "../../hooks/useDiscovery";
import RoomCard from "./components/RoomCard";
import JoinRequestModal from "./components/JoinRequestModal";
import CreateRoomModal from "./CreateRoomModal";
import "./ResearchRoomsList.css";

// ==============================================
// Tab Configuration
// ==============================================
const DISCOVERY_TABS = [
  { id: "forYou", label: "For You", icon: FiStar, color: "purple" },
  { id: "trending", label: "Trending", icon: FiTrendingUp, color: "green" },
  { id: "hot", label: "Hot", icon: FiZap, color: "amber" },
  { id: "new", label: "New", icon: FiClock, color: "blue" },
  { id: "network", label: "Network", icon: FiUsers, color: "pink" },
  { id: "similar", label: "Similar", icon: FiTarget, color: "cyan" },
];

// ==============================================
// Sub-Components
// ==============================================

// Header
const DiscoveryHeader = ({ isMyRoomsView, onCreateRoom, navigate }) => (
  <header className="rdisc-header">
    <div className="rdisc-header-left">
      <div className="rdisc-header-text">
        <h1 className="rdisc-title">
          {isMyRoomsView ? "My Research Labs" : "Discover Labs"}
        </h1>
        <p className="rdisc-subtitle">
          {isMyRoomsView
            ? "Manage your collaborative spaces"
            : "Find your perfect research collaboration"}
        </p>
      </div>
    </div>
    <div className="rdisc-header-actions">
      <div className="rdisc-view-toggle">
        <button
          className={`rdisc-toggle-btn ${!isMyRoomsView ? "rdisc-toggle-active" : ""}`}
          onClick={() => navigate("/labs")}
        >
          <FiGrid />
          <span>Discover</span>
        </button>
        <button
          className={`rdisc-toggle-btn ${isMyRoomsView ? "rdisc-toggle-active" : ""}`}
          onClick={() => navigate("/labs/my-labs")}
        >
          <FiUser />
          <span>My Labs</span>
        </button>
      </div>
      <button className="rdisc-create-btn" onClick={onCreateRoom}>
        <FiPlus />
        <span>Create Lab</span>
      </button>
    </div>
  </header>
);

// Search Bar with Field Filter
const SearchBar = ({ query, onChange, onClear, onSearch, fields, selectedField, onFieldChange }) => (
  <div className="rdisc-search-row">
    <div className="rdisc-search-container">
      <div className="rdisc-search-wrapper">
        <input
          type="text"
          className="rdisc-search-input"
          placeholder="Search labs by name or description..."
          value={query}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch()}
        />
        {query && (
          <button className="rdisc-search-clear" onClick={onClear}>
            <FiX />
          </button>
        )}
      </div>
    </div>
    <div className="rdisc-field-filter">
      <FiFilter className="rdisc-filter-icon" />
      <select
        className="rdisc-field-select"
        value={selectedField}
        onChange={(e) => onFieldChange(e.target.value)}
      >
        <option value="">All Fields</option>
        {fields && fields.map((field) => (
          <option key={field.field} value={field.field}>
            {field.field} ({field.roomCount})
          </option>
        ))}
      </select>
      <FiChevronDown className="rdisc-select-chevron" />
    </div>
  </div>
);

// Discovery Tabs
const DiscoveryTabs = ({ activeTab, onTabChange }) => (
  <div className="rdisc-tabs">
    {DISCOVERY_TABS.map((tab) => (
      <button
        key={tab.id}
        className={`rdisc-tab ${activeTab === tab.id ? "rdisc-tab-active" : ""}`}
        onClick={() => onTabChange(tab.id)}
        data-color={tab.color}
      >
        <tab.icon className="rdisc-tab-icon" />
        <span className="rdisc-tab-label">{tab.label}</span>
      </button>
    ))}
  </div>
);

// Section Header
const SectionHeader = ({ title, subtitle, icon: Icon, onSeeAll, refreshButton }) => (
  <div className="rdisc-section-header">
    <div className="rdisc-section-left">
      {Icon && <Icon className="rdisc-section-icon" />}
      <div className="rdisc-section-text">
        <h2 className="rdisc-section-title">{title}</h2>
        {subtitle && <p className="rdisc-section-subtitle">{subtitle}</p>}
      </div>
    </div>
    <div className="rdisc-section-right">
      {refreshButton}
      {onSeeAll && (
        <button className="rdisc-see-all" onClick={onSeeAll}>
          See all <FiChevronRight />
        </button>
      )}
    </div>
  </div>
);

// Room Grid
const RoomGrid = ({ rooms, onJoinRequest, showScores, badge }) => {
  if (!rooms || rooms.length === 0) {
    return (
      <div className="rdisc-empty-section">
        <FiLayers className="rdisc-empty-icon" />
        <p>No labs found in this category</p>
      </div>
    );
  }

  return (
    <div className="rdisc-grid">
      {rooms.map((room) => (
        <RoomCard
          key={room._id}
          room={room}
          onJoinRequest={onJoinRequest}
          showScores={showScores}
          badge={badge}
        />
      ))}
    </div>
  );
};



// Skeleton Card for loading states
const SkeletonCard = () => (
  <div className="rdcard rdcard-skeleton">
    <div className="rdcard-image-container">
      <div className="skeleton-shimmer skeleton-image" />
    </div>
    <div className="rdcard-content">
      <div className="rdcard-tags">
        <div className="skeleton-shimmer skeleton-tag" />
        <div className="skeleton-shimmer skeleton-tag skeleton-tag--small" />
      </div>
      <div className="rdcard-info">
        <div className="skeleton-shimmer skeleton-title" />
        <div className="skeleton-shimmer skeleton-description" />
        <div className="skeleton-shimmer skeleton-description skeleton-description--short" />
      </div>
      <div className="rdcard-leader-section">
        <div className="rdcard-leader">
          <div className="skeleton-shimmer skeleton-avatar" />
          <div className="rdcard-leader-info">
            <div className="skeleton-shimmer skeleton-label" />
            <div className="skeleton-shimmer skeleton-name" />
          </div>
        </div>
      </div>
      <div className="rdcard-stats">
        <div className="skeleton-shimmer skeleton-stat" />
        <div className="skeleton-shimmer skeleton-stat" />
      </div>
      <div className="rdcard-actions">
        <div className="skeleton-shimmer skeleton-button" />
      </div>
    </div>
  </div>
);

// Skeleton Grid for loading states
const SkeletonGrid = ({ count = 8 }) => (
  <div className="rdisc-grid">
    {Array.from({ length: count }).map((_, index) => (
      <SkeletonCard key={`skeleton-${index}`} />
    ))}
  </div>
);

// Loading State
const LoadingState = () => (
  <div className="rdisc-loading">
    <div className="rdisc-loading-spinner">
      <FiRefreshCw />
    </div>
    <span>Loading discovery...</span>
  </div>
);

// Error State
const ErrorState = ({ error, onRetry }) => (
  <div className="rdisc-error">
    <div className="rdisc-error-icon">!</div>
    <p className="rdisc-error-message">{error}</p>
    <button className="rdisc-error-retry" onClick={onRetry}>
      <FiRefreshCw />
      <span>Try Again</span>
    </button>
  </div>
);

// Empty State for My Rooms
const EmptyMyRooms = ({ onCreateRoom }) => (
  <div className="rdisc-empty">
    <div className="rdisc-empty-icon-wrapper">
      <FiLayers />
    </div>
    <h3>No labs yet</h3>
    <p>You haven't joined any research labs yet. Start exploring or create your own!</p>
    <button className="rdisc-empty-action" onClick={onCreateRoom}>
      <FiPlus />
      <span>Create a Lab</span>
    </button>
  </div>
);

// ==============================================
// Main Component
// ==============================================
const ResearchRoomsList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // eslint-disable-next-line no-unused-vars
  const { user } = useContext(UserContext);

  const { myRooms, fetchMyRooms, loading: myRoomsLoading } = useRoom();
  const { data, loading, error, activeTab, setActiveTab, refresh, isRefreshing, refetch } = useDiscovery();
  const { query, setQuery, results, loading: searchLoading, searched, search, clearSearch } = useDiscoverySearch();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedField, setSelectedField] = useState("");

  const isMyRoomsView = location.pathname.includes("/my-labs") || location.pathname.includes("/my-rooms");

  // Fetch my rooms when viewing my rooms
  useEffect(() => {
    if (isMyRoomsView) {
      fetchMyRooms();
    }
  }, [isMyRoomsView, fetchMyRooms]);

  // Get tab info and badge
  const getTabConfig = useCallback((tabId) => {
    const configs = {
      forYou: { title: "For You", subtitle: "Personalized recommendations based on your profile", icon: FiStar, badge: null, showScores: true },
      trending: { title: "Trending", subtitle: "Fastest growing labs this week", icon: FiTrendingUp, badge: "trending", showScores: false },
      hot: { title: "Hot Right Now", subtitle: "Most active labs in the last 24 hours", icon: FiZap, badge: "hot", showScores: false },
      new: { title: "Just Created", subtitle: "Fresh labs with high potential", icon: FiClock, badge: "new", showScores: false },
      network: { title: "From Your Network", subtitle: "Labs where people you follow are members", icon: FiUsers, badge: null, showScores: false },
      similar: { title: "Similar to Your Labs", subtitle: "Based on your current lab memberships", icon: FiTarget, badge: null, showScores: true },
    };
    return configs[tabId] || configs.forYou;
  }, []);

  // Get rooms for active tab (with field filter applied)
  const activeRooms = useMemo(() => {
    if (!data) return [];
    const tabMapping = {
      forYou: data.forYou,
      trending: data.trending,
      hot: data.hot,
      new: data.new,
      network: data.fromNetwork,
      similar: data.similar,
    };
    let rooms = tabMapping[activeTab] || [];
    
    // Apply field filter if selected
    if (selectedField) {
      rooms = rooms.filter(room => room.field === selectedField);
    }
    
    return rooms;
  }, [data, activeTab, selectedField]);

  const handleJoinRequest = useCallback((room) => {
    setSelectedRoom(room);
    setShowJoinModal(true);
  }, []);

  const handleCreateRoom = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleRoomCreated = useCallback((newRoom) => {
    setShowCreateModal(false);
    navigate(`/labs/${newRoom._id}`);
  }, [navigate]);

  const handleSearch = useCallback(() => {
    if (query.trim()) {
      search(query);
    }
  }, [query, search]);

  const handleFieldChange = useCallback((field) => {
    setSelectedField(field);
    // Clear search when changing field filter
    if (field) {
      clearSearch();
    }
  }, [clearSearch]);

  const tabConfig = getTabConfig(activeTab);

  // My Rooms View
  if (isMyRoomsView) {
    return (
      <div className="rdisc-container">
        <div className="rdisc-wrapper">
          <DiscoveryHeader
            isMyRoomsView={true}
            onCreateRoom={handleCreateRoom}
            navigate={navigate}
          />

          {myRoomsLoading ? (
            <LoadingState />
          ) : myRooms.length > 0 ? (
            <div className="rdisc-content">
              <SectionHeader
                title="Your Labs"
                subtitle={`${myRooms.length} lab${myRooms.length !== 1 ? "s" : ""}`}
                icon={FiUser}
              />
              <RoomGrid rooms={myRooms} onJoinRequest={handleJoinRequest} />
            </div>
          ) : (
            <EmptyMyRooms onCreateRoom={handleCreateRoom} />
          )}
        </div>

        {/* Modals */}
        {showJoinModal && selectedRoom && (
          <JoinRequestModal
            room={selectedRoom}
            onClose={() => {
              setShowJoinModal(false);
              setSelectedRoom(null);
            }}
          />
        )}
        {showCreateModal && (
          <CreateRoomModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={handleRoomCreated}
          />
        )}
      </div>
    );
  }

  // Discovery View
  return (
    <div className="rdisc-container">
      <div className="rdisc-wrapper">
        <DiscoveryHeader
          isMyRoomsView={false}
          onCreateRoom={handleCreateRoom}
          navigate={navigate}
        />

        <SearchBar
          query={query}
          onChange={setQuery}
          onClear={clearSearch}
          onSearch={handleSearch}
          fields={data?.fields}
          selectedField={selectedField}
          onFieldChange={handleFieldChange}
        />

        {/* Search Results */}
        {searched ? (
          <div className="rdisc-content">
            <SectionHeader
              title={`Search Results for "${query}"`}
              subtitle={`${results.length} room${results.length !== 1 ? "s" : ""} found`}
              icon={FiSearch}
            />
            {searchLoading ? (
              <SkeletonGrid count={4} />
            ) : (
              <RoomGrid rooms={results} onJoinRequest={handleJoinRequest} showScores />
            )}
            <button className="rdisc-clear-search" onClick={clearSearch}>
              <FiX /> Clear search and browse discovery
            </button>
          </div>
        ) : loading ? (
          <SkeletonGrid count={8} />
        ) : error ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : (
          <>
            {/* Discovery Tabs */}
            <DiscoveryTabs activeTab={activeTab} onTabChange={setActiveTab} />

            {/* Active Tab Content */}
            <div className="rdisc-content">
              <SectionHeader
                title={tabConfig.title}
                subtitle={tabConfig.subtitle}
                icon={tabConfig.icon}
                refreshButton={
                  <button
                    className={`rdisc-refresh-btn ${isRefreshing ? "rdisc-spinning" : ""}`}
                    onClick={refresh}
                    disabled={isRefreshing}
                  >
                    <FiRefreshCw />
                    <span>Refresh</span>
                  </button>
                }
              />
              <RoomGrid
                rooms={activeRooms}
                onJoinRequest={handleJoinRequest}
                showScores={tabConfig.showScores}
                badge={tabConfig.badge}
              />
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {showJoinModal && selectedRoom && (
        <JoinRequestModal
          room={selectedRoom}
          onClose={() => {
            setShowJoinModal(false);
            setSelectedRoom(null);
          }}
        />
      )}
      {showCreateModal && (
        <CreateRoomModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleRoomCreated}
        />
      )}
    </div>
  );
};

export default ResearchRoomsList;
